/*******************************************************
* VideoPlayer Component
*
* A reusable video player component that randomly selects and plays
* a video from a provided list of video sources
*
* Key Features:
* - Autoplay functionality
* - Muted playback
* - Loop capability
* - Random video selection
* - Responsive fullscreen display
* - Automatic video format detection
********************************************************/
'use client';
//########## IMPORTS ##########
// #region IMPORTS
import { Box } from "@mui/material";
import { selectVideoBySeed } from '@/src/app/(pages)/Login/utils/selectVideoBySeed';
// #endregion

//########## TYPE DEFINITIONS ##########
//#region TYPE DEFINITIONS
interface VideoPlayerProps {
    videos: string[];
    videoSeed: number;
    initialVideo: string | null;
}
//#endregion

//########## CONSTANTS ##########
//#region CONSTANTS
const VIDEO_MIME_TYPES: Record<string, string> = {
    '.webm': 'video/webm',
    '.mp4': 'video/mp4',
};
//#endregion

//########## FUNCTIONS ##########
// #region FUNCTIONS
const getVideoType = (src: string): string => {
    const extension = src.substring(src.lastIndexOf('.')).toLowerCase();
    return VIDEO_MIME_TYPES[extension] || '';
};

//#endregion

export function VideoPlayer({ videos, videoSeed, initialVideo }: VideoPlayerProps) {
    //########## MAIN LOGIC ##########
    // #region MAIN LOGIC
    const videoSrc = selectVideoBySeed(videos, videoSeed) ?? initialVideo;

    if (!videoSrc) return null;
    //#endregion

    //########## MARKDOWN ##########
    // #region MARKDOWN
    return (
        <Box
            sx={{
                position: 'relative',
                width: '100%',
                height: '100%',
                backgroundColor: 'common.black',
            }}
        >
            <video
                key={videoSrc}
                autoPlay
                muted
                loop
                playsInline
                style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    position: 'absolute',
                    backgroundColor: 'common.black',
                }}
                preload="auto"
            >
                <source src={videoSrc} type={getVideoType(videoSrc)} />
            </video>
        </Box>
    );
    //#endregion
}
